interface LicenciaData {
  codigoPostal?: string;
  nombres?: string;
  apellidos?: string;
  domicilio?: string;
  colonia?: string;
  ciudad?: string;
  estado?: string;
}

const estados: string[] = [
  "Aguascalientes",
  "Baja California",
  "Baja California Sur",
  "Chiapas",
  "Chihuahua",
  "Ciudad de Mexico",
  "Coahuila",
  "Colima",
  "Durango",
  "Estado de Mexico",
  "Guanajuato",
  "Guerrero",
  "Hidalgo",
  "Jalisco",
  "Michoacan",
  "Morelos",
  "Nayarit",
  "Nuevo León",
  "Oaxaca",
  "Puebla",
  "Queretaro",
  "San Luis Potosi",
  "Sin Localidad",
  "Sinaloa",
  "Sonora",
  "Tabasco",
  "Tamaulipas",
  "Tlaxcala",
  "Veracruz",
  "Yucatan",
  "Zacatecas",  
];

// Helper: Find closest estado from array
function findClosestEstado(input: string, estados: string[]): string {
  input = input.toLowerCase();
  let bestMatch = "";
  let bestScore = 0;
  for (const estado of estados) {
    const estadoLower = estado.toLowerCase();
    // Score: number of matching characters at start
    let score = 0;
    for (let i = 0; i < Math.min(input.length, estadoLower.length); i++) {
      if (input[i] === estadoLower[i]) score++;
      else break;
    }
    // Or use: if (estadoLower.includes(input)) score = input.length;
    if (score > bestScore) {
      bestScore = score;
      bestMatch = estado;
    }
  }
  return bestMatch;
}

async function parseLicenciaInfo(text: string): Promise<LicenciaData> {
  // Do NOT normalize away newlines, as regexes depend on line structure
  const patterns = {
    // Matches after "C.P." (with or without dots and spaces) and captures 5 digits
    codigoPostal: /(?<=C\.?P\.?\s*:? ?)(\d{5})/,

    // First line after "NOMBRE/NAME" (with or without a colon), capturing uppercase names (including accents and Ñ), stopping at newline or "CURP"
    nombres: /(?<=NOMBRE\/NAME[\s:]*\n)([A-ZÁÉÍÓÚÑ\s]+?)(?=\n|CURP|$)/,

    // Captures everything after the first whitespace in the "NOMBRE/NAME" line as apellidos
    apellidos: /(?<=NOMBRE\/NAME[\s:]*\n[A-ZÁÉÍÓÚÑ]+ )([A-ZÁÉÍÓÚÑ\s]+?)(?=\n|CURP|$)/,

    // Matches the line after "DIRECCIÓN/ADDRESS" (with or without a colon), capturing until a number or end of line
    domicilio: /(?<=DIRECCIÓN\/ADDRESS[\s:]*\n)([^\n\d:]+?)(?=\d|$)/,

    // Captures the line after "DIRECCIÓN/ADDRESS", skipping two lines, then capturing the next line as colonia, stopping before "C.P."
    colonia: /(?<=DIRECCIÓN\/ADDRESS[\s:]*[^\n]*\n[^\n]*\n)([A-ZÁÉÍÓÚÑ\s]+?)(?=\s?C\.?P\.?)/,

    // Captures the third line after "DIRECCIÓN/ADDRESS" as ciudad, stopping at comma or newline
    ciudad: /(?<=DIRECCIÓN\/ADDRESS[\s:]*[^\n]*\n[^\n]*\n[^\n]*\n)([A-ZÁÉÍÓÚÑ\s]+?)(?=,|\n|$)/,

    // Captures after the first comma on the third line after "DIRECCIÓN/ADDRESS" as estado
    estado: /(?<=DIRECCIÓN\/ADDRESS[\s:]*[^\n]*\n[^\n]*\n[^\n]*\n[A-ZÁÉÍÓÚÑ\s]+,\s*)([A-ZÁÉÍÓÚÑ\s]+)/,
  };

  const result: LicenciaData = {};

  for (const [key, pattern] of Object.entries(patterns)) {
    const match = text.match(pattern);
    result[key as keyof LicenciaData] = match ? match[1].trim() : "";
  }

  // Extract the estado candidate string (from the address block)
  const ciudadEstadoMatch = text.match(/DIRECCIÓN\/ADDRESS[\s:]*[^\n]*\n[^\n]*\n[^\n]*\n([A-ZÁÉÍÓÚÑ\s]+),\s*([A-ZÁÉÍÓÚÑ\s]+)/);
  let estadoCandidate = "";
  if (ciudadEstadoMatch && ciudadEstadoMatch[2]) {
    estadoCandidate = ciudadEstadoMatch[2].trim();
  }

  // Instead of regex for estado, use closest match from array
  if (estadoCandidate) {
    result.estado = findClosestEstado(estadoCandidate, estados);
  } else {
    result.estado = "";
  }

  // Fix nombres/apellidos logic for your specific format
  if (result.nombres) {
    const nameParts = result.nombres.trim().split(/\s+/);
    if (nameParts.length >= 4) {
      result.nombres = nameParts.slice(0, -2).join(' ') + ' ';
      result.apellidos = nameParts.slice(-2).join(' ');
    } else if (nameParts.length === 3) {
      result.nombres = nameParts[0] + ' ';
      result.apellidos = nameParts.slice(1).join(' ');
    } else if (nameParts.length === 2) {
      result.nombres = nameParts[0] + ' ';
      result.apellidos = nameParts[1];
    } else {
      result.nombres = result.nombres + ' ';
      result.apellidos = '';
    }
  }

  return result;
}

export default parseLicenciaInfo;